"use client";

import * as React from "react";
import { BottomSheet } from "./bottom-sheet";
import { SHARE_SHEET_CLASS_NAME } from "./bottom-sheet.constants";
import { cn } from "../../lib/utils";
import { ShareSettingsContent } from "./share-settings-content";
import {
  ShareOverview,
  type ShareMode,
  type ShareJobData,
} from "./share-overview";
import { ShareInstructionsContent } from "./share-instructions-content";
import { getPlatformInstructions } from "./share-platform-instructions";
import { Toast, useToast } from "./toast";

// Share option type
export interface ShareOptionInstructions {
  title: string;
  gradient?: string;
  steps: React.ReactNode[];
  helperText?: React.ReactNode;
  linkValue?: string;
  actionLabel?: string;
  actionIcon?: React.ReactNode;
  onActionClick?: () => void;
}

export interface ShareInstructionConfirmationAction {
  label: string;
  onClick?: () => void;
}

export interface ShareDirectInstruction {
  icon?: React.ReactNode;
  instructions: ShareOptionInstructions;
  confirmationAction?: ShareInstructionConfirmationAction;
}

export interface ShareOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  className?: string;
  profileUrl?: string;
  onClick?: () => void;
  instructions?: ShareOptionInstructions;
}

// Linked platform type
export interface LinkedPlatform {
  id: string;
  label: string;
  icon: React.ReactNode;
  instructions?: ShareOptionInstructions;
  onClick?: () => void;
  profileUrl?: string;
}

export interface ShareBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Share mode - profile or job */
  mode?: ShareMode;
  /** Job data required when mode is "job" */
  jobData?: ShareJobData;
  profileName: string;
  profileHandle: string;
  profileAvatar?: string;
  shareLink: string;
  linkedPlatforms?: LinkedPlatform[];
  shareOptions?: ShareOption[];
  onCopyLink?: () => void;
  onEditProfile?: () => void;
  className?: string;
  /** Custom title for the share section */
  title?: string;
  /** Optional subtitle shown with the platforms-only selector title. */
  subtitle?: string;
  /** Hide the edit settings button */
  hideEditButton?: boolean;
  /** Hide the settings view (for job sharing) */
  hideSettings?: boolean;
  /** Theme color for the share card - from SEO settings */
  themeColor?: string;
  /** Callback when theme color changes - should save via SEO API */
  onThemeColorChange?: (color: string) => void;
  /** Optional custom preview card shown above the share link row */
  previewCard?: React.ReactNode;
  /** Opens the sheet directly on a single platform's instructions. */
  directInstruction?: ShareDirectInstruction;
  /** Optional confirmation shown after any selected platform's instructions. */
  instructionConfirmationAction?: ShareInstructionConfirmationAction;
  /** Show only the social platform selector in the overview. */
  platformsOnly?: boolean;
  /** Custom tip notice element shown below title/subtitle */
  tipNotice?: React.ReactNode;
  /** Optional custom action/element rendered directly below the share link row */
  belowShareLink?: React.ReactNode;
}

export function ShareBottomSheet({
  isOpen,
  onClose,
  mode = "profile",
  jobData,
  profileName,
  profileHandle,
  profileAvatar,
  shareLink,
  linkedPlatforms = [],
  shareOptions = [],
  onCopyLink,
  onEditProfile,
  className,
  title,
  subtitle,
  hideEditButton = false,
  hideSettings = false,
  themeColor = "#FFF8E8",
  onThemeColorChange,
  previewCard,
  directInstruction,
  instructionConfirmationAction,
  platformsOnly = false,
  tipNotice,
  belowShareLink,
}: ShareBottomSheetProps) {
  const [view, setView] = React.useState<"share" | "settings" | "instructions">(
    directInstruction ? "instructions" : "share",
  );
  const [currentThemeColor, setCurrentThemeColor] = React.useState(themeColor);
  const [activeInstruction, setActiveInstruction] = React.useState<{
    icon?: React.ReactNode;
    instructions: ShareOptionInstructions;
  } | null>(directInstruction ?? null);

  const { toast, showToast, hideToast } = useToast();

  // Use centralized platform instructions
  const getDefaultPlatformInstructions = React.useCallback(
    (platformId: string, profileUrl?: string) => {
      return getPlatformInstructions(platformId, shareLink, profileUrl);
    },
    [shareLink],
  );

  React.useEffect(() => {
    if (isOpen) {
      setView(directInstruction ? "instructions" : "share");
      setActiveInstruction(directInstruction ?? null);
    }
  }, [directInstruction, isOpen]);

  React.useEffect(() => {
    setCurrentThemeColor(themeColor);
  }, [themeColor]);

  const handleCopyLink = React.useCallback(() => {
    void navigator.clipboard.writeText(shareLink);
    onCopyLink?.();
    showToast("Link copied to clipboard!");
  }, [onCopyLink, shareLink, showToast]);

  const handleThemeColorChange = React.useCallback(
    (color: string) => {
      setCurrentThemeColor(color);
      onThemeColorChange?.(color);
    },
    [onThemeColorChange],
  );

  const handleShareOptionSelect = (option: ShareOption) => {
    // Get instructions from option or fetch default instructions
    const instructionsPayload =
      option.instructions ??
      getDefaultPlatformInstructions(option.id, option.profileUrl);

    if (instructionsPayload) {
      setActiveInstruction({
        icon: option.icon,
        instructions: instructionsPayload,
      });
      setView("instructions");
    }
    option.onClick?.();
  };

  const handlePlatformSelect = (platform: LinkedPlatform) => {
    const instructionsPayload =
      platform.instructions ??
      getDefaultPlatformInstructions(platform.id, platform.profileUrl);

    if (instructionsPayload) {
      setActiveInstruction({
        icon: platform.icon,
        instructions: instructionsPayload,
      });
      setView("instructions");
    }
    platform.onClick?.();
  };

  const handleEditSettings = () => {
    if (!hideSettings) {
      setView("settings");
      onEditProfile?.();
    }
  };

  const activeConfirmationAction =
    directInstruction?.confirmationAction ?? instructionConfirmationAction;

  return (
    <>
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        // title={sheetTitle}
        className={cn(SHARE_SHEET_CLASS_NAME, className)}
      >
        <div className="relative">
          {view === "share" && (
            <ShareOverview
              mode={mode}
              jobData={jobData}
              profileName={profileName}
              profileHandle={profileHandle}
              profileAvatar={profileAvatar}
              shareLink={shareLink}
              themeColor={currentThemeColor}
              linkedPlatforms={linkedPlatforms}
              shareOptions={shareOptions}
              onCopyLink={handleCopyLink}
              onEditSettings={hideSettings ? undefined : handleEditSettings}
              onLinkedPlatformSelect={handlePlatformSelect}
              onShareOptionSelect={handleShareOptionSelect}
              hideEditButton={hideEditButton || hideSettings}
              previewCard={previewCard}
              platformsOnly={platformsOnly}
              title={title}
              subtitle={subtitle}
              tipNotice={tipNotice}
              belowShareLink={belowShareLink}
            />
          )}
          {view === "settings" && !hideSettings && (
            <ShareSettingsContent
              profileName={profileName}
              profileHandle={profileHandle}
              profileAvatar={profileAvatar}
              themeColor={currentThemeColor}
              setThemeColor={handleThemeColorChange}
              onBack={() => setView("share")}
            />
          )}
          {view === "instructions" && activeInstruction && (
            <ShareInstructionsContent
              profileName={profileName}
              profileHandle={profileHandle}
              profileAvatar={profileAvatar}
              shareLink={shareLink}
              icon={activeInstruction.icon}
              instructions={activeInstruction.instructions}
              onBack={() => {
                if (directInstruction) {
                  onClose();
                  return;
                }
                setActiveInstruction(null);
                setView("share");
              }}
              onCopyLink={handleCopyLink}
              onEditSettings={hideSettings ? undefined : handleEditSettings}
              onShowToast={showToast}
              confirmationAction={
                activeConfirmationAction
                  ? {
                      label: activeConfirmationAction.label,
                      onClick: () => {
                        activeConfirmationAction.onClick?.();
                        onClose();
                      },
                    }
                  : undefined
              }
            />
          )}
        </div>
      </BottomSheet>

      <Toast
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </>
  );
}
